import React, { useState } from 'react';
import { connect } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { compose } from 'redux';
import AppButton from '../../../../components/app-button';
import RegisterHeader from '../../../../components/headers/register-header';
import Header1 from '../../../../components/headers/header1';
import { Column, Row } from '../../../../components/layout';
import AppTextInput from '../../../../components/input/app-text-input';
import AppSpinner from '../../../../components/app-spinner';
import NewErrorMessage from '../../../../components/new-error-message';
import { Icons } from '../../../../util/icons';
import colors from '../../../../util/colors';
import * as fonts from '../../../../util/fonts';
import { strings } from '../../../../util/strings';
import { validateEmail } from '../../../../util/utility';
import { addToWizard } from '../../../../store/actions';
import { post } from '../../../../api/api';
import { uriRequestActivationCode } from '../../../../api/endpoints';
import './styles.scss';

interface NewEmailProps {
  wizard: any;
  globalStyles: any;
  labels?: typeof strings.pt.register;
  messageLabels?: typeof strings.pt.messages;
  addToWizard: (value: any) => void;
}

function NewEmail({
  wizard,
  globalStyles,
  labels,
  messageLabels,
  addToWizard,
}: NewEmailProps) {
  const history = useHistory();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const primaryColor = globalStyles?.primaryColor || colors.blue;

  const handleSendCode = async () => {
    if (loading) return;

    const cleanEmail = email.trim();

    if (!cleanEmail) {
      setErrorMessage(
        messageLabels?.requiredField ||
          strings.pt.messages.requiredField,
      );
      return;
    }

    if (!validateEmail(cleanEmail)) {
      setErrorMessage(
        messageLabels?.invalidEmailFormat ||
          strings.pt.messages.invalidEmailFormat,
      );
      return;
    }

    setErrorMessage(null);
    const sessionId =
      wizard?.dataValidation?.currentUser?.sessionId ||
      wizard?.activateRegistration?.guid ||
      '';

    const fullName = wizard?.insuranceValidation?.name || '';

    addToWizard({
      dataValidation: {
        ...wizard?.dataValidation,
        selectedContact: cleanEmail,
        selectedContactType: 'EMAIL',
        isNewEmail: true,
        newEmail: cleanEmail,
        useSessionEmail: false,
      },
    });

    const payload = {
      destination: cleanEmail,
      sessionId: sessionId,
      senderType: 'EMAIL',
      name: fullName,
    };

    try {
      setLoading(true);
      const res: any = await post(
        uriRequestActivationCode(),
        JSON.stringify(payload),
        false,
        'Register-NewEmail-SendCode',
      );

      setLoading(false);

      if (
        res?.result?.status === 'Success' ||
        res?.status === 200 ||
        !res?.error
      ) {
        history.push('/registo/codigo-validacao/email');
      } else {
        const errorText = res?.result?.message;
        const errorCode = res?.result?.code;
        const isAlreadyRegistered = errorCode === 'activation-service-00002';

        setErrorMessage(
          isAlreadyRegistered
            ? labels?.register_email_already_registered ||
                strings.pt.register.register_email_already_registered
            : errorText ||
                messageLabels?.general_errorMessage ||
                labels?.register_phone_activation_resend_error,
        );
      }
    } catch (error: any) {
      setLoading(false);
      const errorCode =
        error?.response?.data?.result?.code ||
        error?.result?.code ||
        error?.code;
      const errorText =
        error?.response?.data?.result?.message ||
        error?.result?.message ||
        error?.message;
      const isAlreadyRegistered = errorCode === 'activation-service-00002';

      const errorMsg = isAlreadyRegistered
        ? labels?.register_email_already_registered ||
            strings.pt.register.register_email_already_registered
        : errorText ||
            messageLabels?.general_errorMessage ||
            labels?.register_phone_activation_resend_error;
      setErrorMessage(errorMsg);
    }
  };

  return (
    <Column className="register-main-column">
      <RegisterHeader />
      <Column className="register-container" style={{ alignItems: 'center' }}>
        <Row
          className="register-header-row"
          style={{ justifyContent: 'center', marginTop: '2.5rem' }}
        >
          <Header1
            style={{
              ...fonts.XL,
              ...fonts.Bold,
              color: colors.black,
              textAlign: 'center',
            }}
          >
            {labels?.register_email_title ||
              strings.pt.register.register_email_title}
          </Header1>
        </Row>

        <Column
          className="register-existing-user-text"
          style={{
            ...fonts.M,
            alignItems: 'center',
            textAlign: 'center',
            maxWidth: '380px',
            width: '100%',
          }}
        >
          <p
            style={{
              ...fonts.M,
              marginBottom: '2.5rem',
              textAlign: 'center',
              color: colors.darkGrey,
              lineHeight: '1.4',
            }}
          >
            {labels?.register_email_description ||
              strings.pt.register.register_email_description}
          </p>

          {errorMessage && (
            <Row
              style={{
                width: '100%',
                marginBottom: '1.5rem',
                justifyContent: 'center',
              }}
            >
              <NewErrorMessage
                iconName={Icons.ALERT_PROBLEM}
                colorIcon={colors.alertRed}
                text={errorMessage}
              />
            </Row>
          )}

          <Row style={{ width: '100%', marginBottom: '2rem' }}>
            <AppTextInput
              value={email}
              onChange={(value: string) => setEmail(value)}
              placeholder={
                labels?.register_email_placeholder ||
                strings.pt.register.register_email_placeholder
              }
              type="email"
              style={{ ...fonts.M, width: '100%' }}
              disabled={loading}
            />
          </Row>

          {loading ? (
            <AppSpinner type="grey" />
          ) : (
            <AppButton
              onClick={handleSendCode}
              disabled={false}
              style={{
                ...fonts.M,
                ...fonts.Bold,
                width: '100%',
                maxWidth: '350px',
                borderRadius: '25px',
                height: '50px',
                backgroundColor: primaryColor,
                color: colors.white,
                cursor: 'pointer',
              }}
            >
              {labels?.register_email_send_code ||
                strings.pt.register.register_email_send_code}
            </AppButton>
          )}
        </Column>
      </Column>
    </Column>
  );
}

const mapStateToProps = ({ stringsDefault, wizard, globalStyles }: any) => ({
  labels: stringsDefault?.value?.pt?.register,
  messageLabels: stringsDefault?.value?.pt?.messages,
  wizard: wizard.value,
  globalStyles: globalStyles.value,
});

const mapDispatchToProps = (dispatch: any) => ({
  addToWizard: (value: any) => dispatch(addToWizard(value)),
});

export default compose(connect(mapStateToProps, mapDispatchToProps))(NewEmail);
